// The badge detail: a bottom sheet on mobile, a dialog on desktop. Big idle art,
// name, description, the tier (the rarity) and how many hold it, when it was
// earned, the copy history of a repeatable, and (on the member's own badges)
// Pin / Unpin and Share.
import React, { useRef } from "react";
import { toast } from "react-toastify";
import { MobileSheet } from "../../../ui/components/MobileSheet";
import { Dialog, DialogContent, DialogTitle } from "../../../ui/components/Dialog";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useSetBadgePinsMutation } from "../../../services/rtkApi/badgesApi";
import { BadgeArt } from "../art";
import type { BadgeCopy, BadgeDef, CategoryProgress, HeldBadge } from "../types";
import { totalValueOf, valueOf } from "../value";
import { useShareBadge } from "../share/useShareBadge";
import { TierLineView } from "../TierName";
import { ProgressBar } from "./BadgeTile";
import {
  MAX_PINS,
  familyLabel,
  fmtDate,
  fmtPoints,
  nextPins,
  ordinal,
  pointsText,
  progressText,
  tierColor,
  tierLine,
  worthText,
} from "./badgeFormat";
import "./badgeCollection.css";

/** A badge as the detail needs it: a catalogue entry, plus the held fields when held. */
export type DetailBadge = BadgeDef &
  Partial<Pick<HeldBadge, "count" | "totalValue" | "firstEarnedAt" | "lastEarnedAt" | "pinned">> & {
    copies?: BadgeCopy[];
  };

export interface BadgeDetailProps {
  badge: DetailBadge;
  /** Held by the member whose badges these are. */
  held: boolean;
  /** "own": the member's own collection (pins, share, copy history). "member": someone else's shelf. */
  mode: "own" | "member";
  /** The member's current pins (own mode). */
  pinned?: readonly string[];
  /** The next-tier progress, for a badge not held yet. */
  progress?: CategoryProgress | null;
  /** Whose shelf this is (member mode). */
  ownerName?: string;
  /** The element for the name (the sheet passes the dialog title). */
  titleAs?: React.ElementType;
}

const COPY_LIST_MAX = 30;

function CopyHistory({ copies, points }: { copies: BadgeCopy[]; points: number }) {
  const shown = copies.slice(0, COPY_LIST_MAX);
  return (
    <div className="bcol-detail__copies">
      <h3 className="bcol-detail__label">Every time you earned it</h3>
      <ol className="bcol-copies">
        {shown.map((c) => (
          <li key={c.copyId} className="bcol-copies__row">
            <span className="bcol-copies__index">{ordinal(c.repeatIndex || 1)}</span>
            <span className="bcol-copies__date">{fmtDate(c.dateEarned)}</span>
            {c.firstEver && <span className="bcol-copies__tag">first on the site</span>}
            {c.backfill && <span className="bcol-copies__tag bcol-copies__tag--muted">from your history</span>}
            <span className="bcol-copies__value">+{fmtPoints(valueOf({ ...c, points }))}</span>
          </li>
        ))}
      </ol>
      {copies.length > shown.length && <p className="bcol-detail__small">and {copies.length - shown.length} more</p>}
    </div>
  );
}

export const BadgeDetail: React.FC<BadgeDetailProps> = ({
  badge,
  held,
  mode,
  pinned = [],
  progress,
  ownerName,
  titleAs: Title = "h2",
}) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [setPins, { isLoading: pinning }] = useSetBadgePinsMutation();
  const { share, isLoading: sharing } = useShareBadge();

  const count = held ? Math.max(1, badge.count || 1) : 0;
  const isPinned = pinned.includes(badge.code);
  const pinsFull = !isPinned && pinned.length >= MAX_PINS;
  const copies = badge.copies ?? [];
  const firstEver = copies.some((c) => c.firstEver);
  // the tier is the rarity; a badge not held never says "Nobody else has it"
  const line = tierLine({
    tier: badge.tier,
    holders: badge.rarity?.holders ?? 0,
    members: badge.rarity?.members ?? 0,
    unique: badge.unique,
    held,
  });

  const togglePin = async () => {
    const codes = nextPins(pinned, badge.code, !isPinned);
    if (!codes) return;
    try {
      await setPins({ codes }).unwrap();
    } catch {
      toast.error(isPinned ? "Could not unpin it. Try again." : "Could not pin it. Try again.");
    }
  };

  // what every copy is worth together; a repeat is worth 4.5% less than the copy before it
  const worth = held && count > 1 ? worthText(totalValueOf({ ...badge, count }), count) : null;

  let earned: string | null = null;
  if (held && badge.firstEarnedAt) {
    const subject = mode === "member" && ownerName ? `${ownerName} earned it` : "Earned";
    const latest = badge.lastEarnedAt ? `, latest on ${fmtDate(badge.lastEarnedAt)}` : "";
    earned =
      count > 1
        ? `${subject} ${count} times. First on ${fmtDate(badge.firstEarnedAt)}${latest}.`
        : `${subject} on ${fmtDate(badge.firstEarnedAt)}.`;
  }

  return (
    <div className="bcol-detail" style={{ "--bcol-tier": tierColor(badge.tier) } as React.CSSProperties}>
      <div className="bcol-detail__stage" data-tier={badge.tier}>
        <BadgeArt
          category={badge.category}
          tier={badge.tier}
          number={badge.number}
          count={count}
          code={badge.code}
          name={badge.name}
          size={isMobile ? 176 : 216}
          variant="full"
          animate={held ? "idle" : "none"}
          locked={!held}
        />
      </div>

      <div className="bcol-detail__kicker">
        {familyLabel(badge.family)} · {pointsText(badge.points)}
      </div>
      <Title className="bcol-detail__name">{badge.name}</Title>
      {badge.description && <p className="bcol-detail__desc">{badge.description}</p>}

      <p className="bcol-detail__rarity" data-testid="badge-tier-line">
        <TierLineView line={line} />
      </p>
      {badge.unique && <p className="bcol-detail__small">One of a kind. Only one member can ever hold it.</p>}
      {held && firstEver && mode === "own" && <p className="bcol-detail__small">You were the first on the site to earn it.</p>}

      {earned && <p className="bcol-detail__earned">{earned}</p>}
      {worth && <p className="bcol-detail__worth">{worth}.</p>}

      {!held && (
        <div className="bcol-detail__locked">
          <p className="bcol-detail__earned">Not earned yet.</p>
          {progress?.next?.code === badge.code && (
            <>
              <ProgressBar pct={Math.max(0, Math.min(100, Math.round(progress.pct || 0)))} label={progressText(progress)} />
              <p className="bcol-detail__small">{progressText(progress)}</p>
            </>
          )}
        </div>
      )}

      {mode === "own" && held && copies.length > 1 && <CopyHistory copies={copies} points={badge.points} />}

      {mode === "own" && held && (
        <div className="bcol-detail__actions">
          <button
            type="button"
            className={`bcol-btn${isPinned ? "" : " bcol-btn--pin"}`}
            onClick={togglePin}
            disabled={pinning || pinsFull}
            aria-pressed={isPinned}
          >
            {isPinned ? "Unpin" : "Pin to profile"}
          </button>
          <button type="button" className="bcol-btn bcol-btn--share" onClick={() => share(badge.code)} disabled={sharing}>
            {sharing ? "Making the link" : "Share"}
          </button>
        </div>
      )}
      {mode === "own" && held && pinsFull && (
        <p className="bcol-detail__small" role="note">
          You can pin {MAX_PINS} badges. Unpin one to pin this one.
        </p>
      )}
    </div>
  );
};

export interface BadgeDetailSheetProps extends Omit<BadgeDetailProps, "badge" | "titleAs"> {
  badge: DetailBadge | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** BadgeDetail in a MobileSheet (up to 700 px wide) or a dialog. */
const BadgeDetailSheet: React.FC<BadgeDetailSheetProps> = ({ badge, open, onOpenChange, ...rest }) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  // keep the last badge on screen while the sheet animates out
  const last = useRef<DetailBadge | null>(null);
  if (badge) last.current = badge;
  const shown = badge ?? last.current;
  const content = shown ? <BadgeDetail badge={shown} titleAs={DialogTitle} {...rest} /> : null;

  if (isMobile) {
    return (
      <MobileSheet
        open={open && !!shown}
        onOpenChange={onOpenChange}
        fitContent
        maxHeight="88vh"
        showCloseButton
        title={shown ? familyLabel(shown.family) : undefined}
        contentClassName="bcol-sheet"
      >
        {content}
      </MobileSheet>
    );
  }
  return (
    <Dialog open={open && !!shown} onOpenChange={onOpenChange}>
      <DialogContent className="bcol-dialog" aria-describedby={undefined}>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default BadgeDetailSheet;
